import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { ElMessage } from 'element-plus'
import { getApiBaseURL } from '@/config/api'

// 创建axios实例
const apiClient: AxiosInstance = axios.create({
  baseURL: getApiBaseURL(),
  timeout: 80000, // 增加超时时间到80秒
  headers: {
    'Content-Type': 'application/json',
  },
})

// 心跳检测配置
let heartbeatInterval: NodeJS.Timeout | null = null
let isOnline = true
let reconnectAttempts = 0
const MAX_RECONNECT_ATTEMPTS = 5

// 开始心跳检测
const startHeartbeat = () => {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval)
  }
  
  heartbeatInterval = setInterval(async () => {
    try {
      await apiClient.get('/health', { timeout: 5000 })
      if (!isOnline) {
        console.log('🟢 后端服务已恢复连接')
        isOnline = true
        reconnectAttempts = 0
        ElMessage.success('服务连接已恢复')
      }
    } catch (error) {
      if (isOnline) {
        console.log('🔴 后端服务连接断开')
        isOnline = false
        ElMessage.warning('服务连接断开，正在尝试重连...')
      }
      
      // 尝试重连
      if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
        reconnectAttempts++
        console.log(`🔄 尝试重连 (${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})`)
        setTimeout(() => {
          startHeartbeat()
        }, 5000 * reconnectAttempts) // 递增重连间隔
      } else {
        console.log('❌ 重连失败，请手动刷新页面')
        ElMessage.error('服务连接失败，请刷新页面重试')
      }
    }
  }, 30000) // 每30秒检测一次
}

// 停止心跳检测
const stopHeartbeat = () => {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval)
    heartbeatInterval = null
  }
}

// 请求拦截器
apiClient.interceptors.request.use(
  (config: AxiosRequestConfig) => {
    // 添加认证token
    const token = localStorage.getItem('token')
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    // 添加请求时间戳，避免缓存
    if (config.params) {
      config.params._t = Date.now()
    } else {
      config.params = { _t: Date.now() }
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 响应拦截器
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // 重置重连计数
    reconnectAttempts = 0
    
    // 检查响应状态
    if (response.data && response.data.code !== undefined) {
      if (response.data.code === 200) {
        return response.data
      } else {
        // 404错误不显示错误消息（由业务层处理）
        if (response.data.code === 404) {
          return Promise.reject({ response: { status: 404, data: response.data } })
        }
        ElMessage.error(response.data.message || '请求失败')
        return Promise.reject(new Error(response.data.message || '请求失败'))
      }
    }
    
    return response.data
  },
  (error) => {
    console.error('API请求错误:', error)
    
    // 处理网络错误
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      ElMessage.error('请求超时，请检查网络连接')
    } else if (error.response) {
      // 服务器响应错误
      const { status, data } = error.response
      
      switch (status) {
        case 401:
          ElMessage.error('登录已过期，请重新登录')
          localStorage.removeItem('token')
          window.location.href = '/login'
          break
        case 403:
          ElMessage.error('权限不足')
          break
        case 404:
          ElMessage.error('请求的资源不存在')
          break
        case 500:
          ElMessage.error('服务器内部错误')
          break
        default:
          ElMessage.error(data?.message || `请求失败 (${status})`)
      }
    } else if (error.request) {
      // 网络错误
      ElMessage.error('网络连接失败，请检查网络设置')
    } else {
      ElMessage.error('请求配置错误')
    }
    
    return Promise.reject(error)
  }
)

// 页面加载时开始心跳检测
if (typeof window !== 'undefined') {
  // 页面可见性变化时处理
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      stopHeartbeat()
    } else {
      startHeartbeat()
    }
  })
  
  // 页面加载完成后开始心跳
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startHeartbeat)
  } else {
    startHeartbeat()
  }
  
  // 页面卸载时停止心跳
  window.addEventListener('beforeunload', stopHeartbeat)
}

// 创建request对象，包含常用的HTTP方法
const request = {
  get: <T = any>(url: string, config?: any) => apiClient.get<T>(url, config),
  post: <T = any>(url: string, data?: any, config?: any) => apiClient.post<T>(url, data, config),
  put: <T = any>(url: string, data?: any, config?: any) => apiClient.put<T>(url, data, config),
  delete: <T = any>(url: string, config?: any) => apiClient.delete<T>(url, config),
  patch: <T = any>(url: string, data?: any, config?: any) => apiClient.patch<T>(url, data, config)
}

export default request
export { apiClient } 